import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import { FC, PropsWithChildren, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icons, InternalButtonLink, Svgs } from ".";
import { useTypedTranslation } from "../hooks";
import { useLogout } from "../hooks/useLogout";
import { BottomNavigation } from "./BottomNavigation";
import { BodyTextS, HeadingS } from "./Typography";

export type BarProps = PropsWithChildren<{
    onLogoClick?: () => void;
}>;

const EmptyBar: FC<BarProps> = ({ children, onLogoClick }) => {
    return (
        <Toolbar
            sx={{
                justifyContent: "space-between",
                height: "80px",
                px: { xs: 0 },
            }}
        >
            <Link onClick={onLogoClick} sx={{ lineHeight: 0 }}>
                <Svgs.Logo sx={{ width: 61, height: 34 }} />
            </Link>
            {children}
        </Toolbar>
    );
};

export const LoggedOutBar: FC = () => {
    const { t } = useTypedTranslation();

    return (
        <EmptyBar>
            <InternalButtonLink
                startIcon={<Icons.Phone sx={{ width: "1.7rem", height: "2.4rem" }} />}
                customVariant="appBarLink"
                size="small"
                href={t("appBar.contact.phoneLink")}
                sx={{
                    color: "text.primary",
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                }}
            >
                {t("appBar.contact.phone")}
            </InternalButtonLink>
        </EmptyBar>
    );
};

const LogoutButton = () => {
    const { click } = useLogout();
    const { t } = useTypedTranslation();

    return (
        <InternalButtonLink
            // variant="appBarLink"
            sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: "inherit",
            }}
            onClick={click}
            startIcon={<Icons.LogOut sx={{ width: "1.6rem", height: "2.1rem", mr: 2 }} />}
            data-test="logout-button"
        >
            {t("appBar.logout.button")}
        </InternalButtonLink>
    );
};

export const LoggedInBar: FC = () => {
    return (
        <EmptyBar>
            <Box sx={{ display: { md: "none" }, flexGrow: 1 }} />
            <BottomNavigation variant="desktop" />
            <LogoutButton />
        </EmptyBar>
    );
};

export const PortalAppBar: FC = () => {
    const context = useMsal();
    const isAuthenticated = useIsAuthenticated();
    if (context.inProgress !== InteractionStatus.Startup) {
        if (isAuthenticated) {
            return (
                <LoggedInBar
                // BottomNavigationComponent={BottomNavigationDesktopComponent}
                />
            );
        } else {
            return <LoggedOutBar />;
        }
    } else {
        return (
            <EmptyBar>
                <Skeleton width={300} />
                <Skeleton width={100} />
            </EmptyBar>
        );
    }
};

interface BackNavigationBarProps {
    id?: string | ReactNode;
    title: ReactNode;
    subtitle?: ReactNode;
    onIconClick?: () => void;
}

export const BackNavigationBar: FC<BackNavigationBarProps> = ({ id, title, subtitle, onIconClick }) => {
    return (
        <Toolbar>
            <IconButton
                aria-label="menu"
                onClick={onIconClick}
                sx={{
                    position: "relative",
                    left: (theme) => `-${theme.spacing(3)}`,
                    color: "transparent",
                }}
            >
                <Icons.Back sx={{ width: "2.2rem", height: "2.2rem" }} color="primary" />
            </IconButton>
            <Stack pt={1} width="100%">
                <HeadingS sx={{ mb: -2, color: "text.primary" }}>{title}</HeadingS>
                {subtitle && <BodyTextS sx={{ mb: -2, color: "grey.dark" }}>{subtitle}</BodyTextS>}
                {typeof id === "string" && <BodyTextS sx={{ mb: -2, color: "grey.dark" }}>{`(${id})`}</BodyTextS>}
            </Stack>
        </Toolbar>
    );
};

// pro stránku uživatel-nenalezen
export const FauxLoggedInBar: FC = () => {
    const { click } = useLogout();
    const { t } = useTranslation();

    return (
        <EmptyBar>
            <InternalButtonLink
                onClick={click}
                customVariant="appBarLink"
                size="small"
                startIcon={<Icons.LogOut sx={{ width: "1.6rem", height: "2.1rem", mr: 2 }} />}
                sx={{
                    pr: 0,
                }}
            >
                {t("appBar.logout.button")}
            </InternalButtonLink>
        </EmptyBar>
    );
};
